/* eslint-disable max-lines-per-function */
import {AjaxAddToCart} from 'extensions/AjaxAddToCart';
import GTM from 'extensions/gtm';
import 'extensions/ui-components/x-capacity-flag';
import {imageFallback} from 'extensions/ui-directives';
import {defineCustomElement} from 'extensions/ui-utils';
import {html, LitElement, nothing} from 'lit';
import {classMap} from 'lit/directives/class-map.js';
import stylesheet from './x-product-card.scss?as=stylesheet';

export class ProductCard extends LitElement {
	static get styles () {
		return [stylesheet];
	}

	static get properties () {
		return {
			_imageLoaded: {
				state: true,
				type: Boolean
			},
			basePrice: {
				attribute: 'base-price',
				type: Number
			},
			capacityCooling: {
				attribute: 'capacity-cooling',
				type: String
			},
			capacityHeating: {
				attribute: 'capacity-heating',
				type: String
			},
			category: {
				type: String
			},
			description: {
				type: String
			},
			formattedBasePrice: {
				attribute: 'formatted-base-price',
				type: String
			},
			formattedPrice: {
				attribute: 'formatted-price',
				type: String
			},
			hidePricing: {
				attribute: 'hide-pricing',
				type: Boolean
			},
			imageHeight: {
				attribute: 'image-height',
				type: Number
			},
			imageUrl: {
				attribute: 'image-url',
				type: String
			},
			imageWidth: {
				attribute: 'image-width',
				type: Number
			},
			list: {
				attribute: 'data-gtm-list',
				type: String
			},
			position: {
				attribute: 'data-gtm-position',
				type: Number
			},
			price: {
				type: Number
			},
			quantity: {
				type: Number
			},
			showAddToCart: {
				attribute: 'show-add-to-cart',
				type: Boolean
			},
			showDetailsLink: {
				attribute: 'show-details-link',
				type: Boolean
			},
			showQuantity: {
				attribute: 'show-quantity',
				type: Boolean
			},
			sku: {
				type: String
			},
			type: {
				type: String
			},
			url: {
				type: String
			}
		};
	}

	get formattedCategory () {
		if (this.category?.trim() !== '') {
			return this.category;
		}

		return html`&nbsp;`;
	}

	constructor () {
		super();

		// Create gtm helper
		this.gtm = new GTM(window.mivaJS);

		// Internal default values

		/**
		 * @type {boolean}
		 */
		this._imageLoaded = false;

		// Default values

		/**
		 * @type {number}
		 */
		this.basePrice = 0;

		/**
		 * @type {string}
		 */
		this.capacityCooling = '';

		/**
		 * @type {string}
		 */
		this.capacityHeating = '';

		/**
		 * @type {string}
		 */
		this.category = '';

		/**
		 * @type {string}
		 */
		this.formattedBasePrice = '';

		/**
		 * @type {string}
		 */
		this.formattedPrice = '';

		/**
		 * @type {boolean}
		 */
		this.hidePricing = false;

		/**
		 * @type {number}
		 */
		this.imageHeight = 0;

		/**
		 * @type {string}
		 */
		this.imageUrl = '';

		/**
		 * @type {number}
		 */
		this.imageWidth = 0;

		/**
		 * @type {string}
		 */
		this.list = '';

		/**
		 * @type {number}
		 */
		this.position = 0;

		/**
		 * @type {number}
		 */
		this.price = 0;

		/**
		 * @type {number}
		 */
		this.quantity = 1;

		/**
		 * @type {boolean}
		 */
		this.showAddToCart = false;

		/**
		 * @type {boolean}
		 */
		this.showDetailsLink = false;

		/**
		 * @type {boolean}
		 */
		this.showQuantity = false;

		/**
		 * @type {string}
		 */
		this.sku = '';

		/**
		 * @type {string}
		 */
		this.description = '';

		/**
		 * @type {string}
		 */
		this.type = '';

		/**
		 * @type {string}
		 */
		this.url = '';
	}

	firstUpdated () {

		if (window.mivaJS.Page_Code !== 'BASK') {
			const relatedProductPurchaseForms = this.renderRoot.querySelectorAll('.js-product-purchase-form');
			for (const form of relatedProductPurchaseForms) {
				new AjaxAddToCart(form);
			}
		}
	}

	handleImageLoad () {
		this._imageLoaded = true;
	}

	/**
	 * @param {Event} e
	 */
	handleQuantityChange (e) {
		const [target] = e.composedPath();

		if (target) {
			this.quantity = target.value;

			this.dispatchEvent(new CustomEvent('change', {
				bubbles: true,
				composed: true,
				detail: {
					code: this.sku,
					quantity: this.quantity
				}
			}));
		}
	}

	handleClick () {
		this.gtm.sendProductClick({
			/* eslint-disable camelcase */
			item_id: this.sku,
			item_name: this.sku,
			/* eslint-enable camelcase */
			list: this.list,
			position: this.position,
			price: this.price
		});
	}

	renderInner () {
		const boundingBoxClasses = {
			'bounding-box': true,
			'is--loaded': this._imageLoaded
		};

		return html`
			<figure class="${classMap(boundingBoxClasses)}">
				<img src="${this.imageUrl}" alt="${this.sku} image" loading="lazy" @load="${this.handleImageLoad}" ${imageFallback(window.mivaJS.Image_Not_Found_Grid)} />
				${this.showQuantity && !this.showAddToCart ? html`<x-quantity @change="${this.handleQuantityChange}" class="quantity" value="${this.quantity}" animate></x-quantity>` : nothing}
			</figure>
			<div class="info-container">
				<div class="capacity-container">
					${this.capacityCooling ? html`<x-capacity-flag type="cooling">${this.capacityCooling}</x-capacity-flag>` : nothing}
					${this.capacityHeating ? html`<x-capacity-flag type="heating">${this.capacityHeating}</x-capacity-flag>` : nothing}
				</div>
				<div class="info">
					<div class="category">${this.formattedCategory}</div>
					<div class="sku">${this.sku}</div>
					${this.description ? html`<div class="description">${this.description}</div>` : nothing}
					${this.renderPrice()}
					<slot name="inventory"></slot>
					${this.showDetailsLink ? html`<a href="${this.url}" class="product-link" @click="${this.handleClick}"><span class="link-text">View Product Details</span><span class="link-icon" aria-hidden="true"></span></a>` : nothing}
					${this.renderAddToCart()}
				</div>
			</div>
		`;
	}

	renderPrice () {
		if (this.type === 'simple') {
			return nothing;
		}

		if (!this.price || this.hidePricing) {
			return html`
				<div class="price-container">
					<span class="price price-hyphen"></span>
				</div>
			`;
		}

		return html`
			<div class="price-container">
				<span class="price">${this.formattedPrice}</span>
				${this.basePrice > this.price ? html`<s class="base-price">${this.formattedBasePrice}</s>` : nothing}
			</div>
		`;
	}

	renderFlags () {
		return html`
			<div class="flags">
				<slot name="flag"></slot>
			</div>
		`;
	}

	renderAddToCart () {
		if (!this.showAddToCart) {
			return nothing;
		}

		return html`
			<form class="js-product-purchase-form" action="${window.mivaJS?.Basket_Url}" method="post">
				<input type="hidden" name="Action" value="ADPR" />
				<input type="hidden" name="Product_Code" value="${this.sku}" />
				<input type="hidden" name="Product_Name" value="${this.sku}" />
				<input type="hidden" name="Product_Price" value="${this.price}" />
				<input type="hidden" name="Quantity" value="${this.quantity}" />
				<input class="product-purchase-button js-add-to-cart" data-action="${window.mivaJS?.Basket_Url}" data-value="Add To Cart" type="submit" value="Add To Cart">
				<div class="js-add-to-cart-message" hidden></div>
			</form>
		`;
	}

	render () {
		if (this.showDetailsLink || !this.url) {
			return html`
				<div class="container">
					${this.renderFlags()}
					${this.renderInner()}
				</div>
			`;
		}

		return html`
			<a href="${this.url}" class="container" @click="${this.handleClick}">
				${this.renderFlags()}
				${this.renderInner()}
			</a>
		`;
	}
};

defineCustomElement('x-product-card', ProductCard);
